import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/auth';
import './CreateMCQPage.css';

const CreateMCQPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [mcqData, setMcqData] = useState({
    topic: '',
    category: '',
    duration: 0,
    startTime: ''
  });

  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [filterModule, setFilterModule] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');

  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [highlightedCardId, setHighlightedCardId] = useState(null);

  const showPopupMessage = (message, type = 'success') => {
    setPopupMessage(message);
    setPopupType(type);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setTimeout(() => {
      setPopupMessage('');
      setPopupType('');
    }, 300);
  };

  const resetForm = () => {
    setMcqData({ topic: '', category: '', duration: 0, startTime: '' });
    setSelectedQuestions([]);
    setSearchTerm('');
    setFilterModule('');
    setFilterCategory('');
    setFilterSubcategory('');
  };

  const filteredQuestions = libraryQuestions.filter(q => {
    const matchesSearch = q.question.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModule = !filterModule || q.module === filterModule;
    const matchesCategory = !filterCategory || q.categoryName === filterCategory;
    const matchesSubcategory = !filterSubcategory || q.subcategory === filterSubcategory;
    return matchesSearch && matchesModule && matchesCategory && matchesSubcategory;
  });

  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const form = document.querySelector('form');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        if (window.confirm('Are you sure you want to reset the form?')) resetForm();
      }

      if (e.key === 'Escape') setSearchTerm('');

      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        setSelectedQuestions([
          ...libraryQuestions.filter(q =>
            filteredQuestions.some(fq => fq._id === q._id) &&
            !selectedQuestions.some(sq => sq._id === q._id)
          ),
          ...selectedQuestions
        ]);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [libraryQuestions, filteredQuestions, selectedQuestions]);

  useEffect(() => {
    const fetchLibrary = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/library-getAllQuestions`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const result = await res.json();
        if (result.success) setLibraryQuestions(result.data);
      } catch (err) {
        console.error("Error fetching library:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLibrary();
  }, [API_BASE_URL]);

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setMcqData({ ...mcqData, [name]: value });
  };

  const toggleQuestionSelection = (question) => {
    const isSelected = selectedQuestions.find(q => q._id === question._id);

    setHighlightedCardId(question._id);
    setTimeout(() => setHighlightedCardId(null), 300);

    if (isSelected) {
      setSelectedQuestions(selectedQuestions.filter(q => q._id !== question._id));
    } else {
      setSelectedQuestions([...selectedQuestions, question]);
    }
  };

  // 🎯 Additional Interactive Features
  const [hoveredSectionId, setHoveredSectionId] = useState(null);
  const [sortBy, setSortBy] = useState('recent');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!mcqData.topic.trim()) return showPopupMessage('Please enter an MCQ topic', 'error');
    if (!mcqData.category) return showPopupMessage('Please select an MCQ type', 'error');
    if (selectedQuestions.length === 0) return showPopupMessage('Please select at least one question', 'error');
    if (mcqData.duration <= 0) return showPopupMessage('Please set a valid duration', 'error');

    const payload = { ...mcqData, questions: selectedQuestions };
    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/mcqs/createmcq`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.topic) {
        showPopupMessage(`✓ MCQ "${mcqData.topic}" created successfully!`, 'success');
        setTimeout(() => {
          resetForm();
          setSubmitting(false);
        }, 1500);
      } else {
        showPopupMessage(result.message || 'Failed to create MCQ', 'error');
        setSubmitting(false);
      }
    } catch (err) {
      showPopupMessage('Error creating MCQ: ' + err.message, 'error');
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loader">Loading Library...</div>;

  return (
    <div className="create-mcq-container">
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className={`popup-modal popup-${popupType}`} onClick={(e) => e.stopPropagation()}>
            <h3>{popupType === 'success' ? 'Success' : 'Error'}</h3>
            <p>{popupMessage}</p>
            <button onClick={closePopup}>OK</button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="section" id="section-1" onMouseEnter={() => setHoveredSectionId(1)} onMouseLeave={() => setHoveredSectionId(null)}>
          <h2>📋 1. MCQ Details</h2>

          <div className="form-row">
            <input name="topic" placeholder="MCQ Topic" onChange={handleMetaChange} value={mcqData.topic} required autoFocus />
            <select name="category" value={mcqData.category} onChange={handleMetaChange} required>
              <option value="">Select MCQ Type</option>
              <option value="Technical">Technical</option>
              <option value="Aptitude">Aptitude</option>
              <option value="Verbal">Verbal</option>
            </select>
          </div>

          <div className="form-row">
            <input type="number" name="duration" value={mcqData.duration} onChange={handleMetaChange} placeholder="Duration (minutes)" min="1" />
            <input type="datetime-local" name="startTime" value={mcqData.startTime} onChange={handleMetaChange} required />
          </div>

          <div className="form-summary">
            <div className="summary-item">
              <span className="summary-label">Topic:</span>
              <span className="summary-value">{mcqData.topic || 'Not set'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Type:</span>
              <span className="summary-value">{mcqData.category || 'Not set'}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Duration:</span>
              <span className="summary-value">{mcqData.duration || 0} min</span>
            </div>
          </div>
        </div>

        <div className={`section section-2 ${hoveredSectionId === 2 ? 'hovered' : ''}`} id="section-2" onMouseEnter={() => setHoveredSectionId(2)} onMouseLeave={() => setHoveredSectionId(null)}>
          <h2>🎯 2. Select Questions</h2>

          <div className="questions-header">
            <div className="selected-count">
              <span className="count-badge">{selectedQuestions.length}</span>
              <span className="count-text">Question{selectedQuestions.length !== 1 ? 's' : ''} Selected</span>
            </div>
            {selectedQuestions.length > 0 && (
              <button 
                type="button" 
                className="clear-selection-btn"
                onClick={() => setSelectedQuestions([])}
                title="Clear all selections"
              >
                ✕ Clear All
              </button>
            )}
          </div>

          <div className="search-filter-container">
            <input
              type="text"
              placeholder="🔍 Search questions... (Ctrl+F)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-enhanced"
            />

            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
              title="Sort questions"
            >
              <option value="recent">Recent</option>
              <option value="difficulty">Difficulty</option>
              <option value="marks">Marks</option>
            </select>
          </div>

          <div className="filter-row">
            <select value={filterModule} onChange={(e) => {
              setFilterModule(e.target.value);
              setFilterCategory('');
              setFilterSubcategory('');
            }} className="filter-select-enhanced">
              <option value="">All Modules</option>
              {[...new Set(libraryQuestions.map(q => q.module))].map(mod => (
                <option key={mod} value={mod}>{mod}</option>
              ))}
            </select>

            <select value={filterCategory} onChange={(e) => {
              setFilterCategory(e.target.value);
              setFilterSubcategory('');
            }} className="filter-select-enhanced">
              <option value="">All Categories</option>
              {[...new Set(libraryQuestions.map(q => q.categoryName))].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select value={filterSubcategory} onChange={(e) => setFilterSubcategory(e.target.value)} className="filter-select-enhanced">
              <option value="">All Subcategories</option>
              {[...new Set(libraryQuestions.map(q => q.subcategory))].map(sub => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>

          <p className="results-info">Found: <strong>{filteredQuestions.length}</strong> question{filteredQuestions.length !== 1 ? 's' : ''}</p>

          <div className="library-picker">
            {filteredQuestions.length > 0 ? (
              filteredQuestions.map((q, index) => {
                const isSelected = selectedQuestions.find(sq => sq._id === q._id);
                const isHighlighted = highlightedCardId === q._id;

                return (
                  <div
                    key={q._id}
                    className={`picker-card ${isSelected ? 'selected' : ''} ${isHighlighted ? 'pulse' : ''}`}
                    onClick={() => toggleQuestionSelection(q)}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    title={q.question}
                  >
                    <div className="picker-header">
                      <span className="module-badge">{q.module}</span>
                      <span className="category-badge">{q.categoryName}</span>
                      <span className="subcategory-badge">{q.subcategory}</span>
                    </div>

                    <p className="question-preview">{q.question}</p>
                    
                    <div className="picker-meta">
                      <span className="mark-badge">{q.mark} Pt</span>
                      <span className={`difficulty-badge difficulty-${q.difficulty?.toLowerCase()}`}>{q.difficulty}</span>
                    </div>

                    {isSelected && <div className="selected-badge">✓ Added</div>}
                  </div>
                );
              })
            ) : (
              <div className="no-results">
                <p>📭 No questions found. Try adjusting your filters.</p>
              </div>
            )}
          </div>
        </div>

        <div className="button-container">
          <button 
            type="button" 
            className="reset-btn"
            onClick={() => {
              if (window.confirm('Reset the entire form?')) resetForm();
            }}
            title="Ctrl+R to reset"
          >
            ↻ Reset
          </button>
          <button type="submit" className="save-mcq-btn" disabled={submitting} title="Ctrl+Enter to submit">
            {submitting ? '⏳ Creating MCQ...' : '✓ Create MCQ'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateMCQPage;